import type {
  WordPressDraftPost,
  WordPressApiDraftListResponse,
  WordPressFeaturedImage,
} from '../types/wordpress';

const WORDPRESS_SITE_URL =
  import.meta.env.VITE_WORDPRESS_SITE_URL ||
  import.meta.env.WORDPRESS_SITE_URL ||
  '';

// Endpoints internos (funções Pages ou proxy Node)
const DRAFT_LIST_ENDPOINT = '/api/wp-drafts';
const DRAFT_POST_ENDPOINT = '/api/wp-draft';

const expectJson = async (res: Response) => {
  const ctype = res.headers.get('content-type') || '';
  if (!ctype.includes('application/json')) {
    const snippet = await res.text();
    throw new Error(
      `Resposta não-JSON do endpoint WP (status ${res.status}). Verifique proxy/env. Snippet: ${snippet.slice(
        0,
        200
      )}`
    );
  }
  return res.json();
};

export async function getDrafts(page = 1, perPage = 20): Promise<WordPressApiDraftListResponse> {
  const url = `${DRAFT_LIST_ENDPOINT}?page=${page}&per_page=${perPage}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Erro ao buscar drafts: ${response.status} ${response.statusText}`);
  }

  return expectJson(response);
}

export async function getDraftBySlug(slug: string): Promise<WordPressDraftPost | null> {
  const response = await fetch(`${DRAFT_POST_ENDPOINT}/${encodeURIComponent(slug)}`);

  if (response.status === 404) return null;

  if (!response.ok) {
    throw new Error(`Erro ao buscar draft: ${response.status} ${response.statusText}`);
  }

  return expectJson(response);
}

export function cleanHtmlContent(html: string): string {
  html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  html = html.replace(/style\s*=\s*["'][^"']*["']/gi, '');
  return html;
}

export function extractTextFromHtml(html: string, maxLength: number = 160): string {
  if (typeof document === 'undefined') return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = div.textContent || div.innerText || '';
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.substring(0, maxLength).trim() + '...';
}

export function getSEOTitle(post: WordPressDraftPost): string {
  return post.rank_math?.final?.title || post.title || post.slug;
}

export function getSEODescription(post: WordPressDraftPost): string {
  return (
    post.rank_math?.final?.description ||
    post.excerpt ||
    extractTextFromHtml(post.content || '', 160)
  );
}

export function getSEOImage(post: WordPressDraftPost): string | null {
  const pick = (imgs?: WordPressFeaturedImage['urls']) =>
    imgs?.large?.url ||
    imgs?.full?.url ||
    imgs?.medium?.url ||
    imgs?.thumbnail?.url ||
    undefined;

  const ogUrls = post.rank_math?.final?.og_image?.urls;
  const featuredUrls = post.featured_image?.urls;

  return (
    pick(ogUrls) ||
    post.rank_math?.final?.facebookImage ||
    post.rank_math?.final?.twitterImage ||
    pick(featuredUrls) ||
    null
  );
}

export function getCanonicalUrl(post: WordPressDraftPost): string {
  return post.rank_math?.final?.canonical || post.link || `${WORDPRESS_SITE_URL}/blog/${post.slug}`;
}

export function getRobots(post: WordPressDraftPost): { index: boolean; follow: boolean } {
  const robots = post.rank_math?.final?.robots || '';
  return {
    index: !robots.includes('noindex'),
    follow: !robots.includes('nofollow'),
  };
}

export { WORDPRESS_SITE_URL };

